/**
 * NewProjectConfirmModal — prompt the writer when the empty-doc detector
 * observes the document body has been cleared, asking whether they want
 * to start a fresh project (rotates AsthrDocumentId) or undo their
 * deletion and keep the existing project state intact.
 *
 * SRP: pure presentation. No state, no Word.js calls, no rotation
 * logic. The shell wires up the actual side-effects.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

const useStyles = makeStyles({
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 10, 30, 0.55)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: fonts.body,
  },
  dialog: {
    background: surfaceColors.paper,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderTop: `3px solid ${brandColors.primary}`,
    borderRadius: "4px",
    padding: "18px 18px 16px",
    maxWidth: "420px",
    width: "100%",
    boxShadow: "0 20px 48px rgba(15, 10, 30, 0.25)",
  },
  title: {
    fontFamily: fonts.display,
    fontSize: "15px",
    fontWeight: 700,
    color: inkColors.ink,
    marginBottom: "6px",
  },
  body: {
    fontSize: "12px",
    color: inkColors.ink2,
    lineHeight: 1.5,
  },
  caveat: {
    fontSize: "10.5px",
    color: inkColors.ink4,
    lineHeight: 1.4,
    marginTop: "10px",
    paddingLeft: "8px",
    borderLeft: `2px solid ${surfaceColors.lineStrong}`,
  },
  actions: {
    display: "flex",
    gap: "8px",
    marginTop: "14px",
  },
  btn: {
    flex: 1,
    fontSize: "12px",
    fontWeight: 700,
    padding: "9px 14px",
    borderRadius: "3px",
    cursor: "pointer",
    fontFamily: fonts.body,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    border: "none",
  },
  btnPrimary: {
    background: brandColors.primary,
    color: "#fff",
  },
  btnSecondary: {
    background: "#fff",
    color: inkColors.ink2,
    border: `1px solid ${surfaceColors.lineStrong}`,
  },
});

export interface NewProjectConfirmModalProps {
  /** Optional context — name of the project being abandoned, shown in
   *  the caveat. Falls back to a generic phrasing when absent. */
  currentProjectLabel?: string | null;
  /** Writer chose "Start fresh" — shell rotates the AsthrDocumentId. */
  onConfirmRotate: () => void;
  /** Writer chose "Cancel" — shell triggers Word undo to restore content. */
  onCancelRestore: () => void;
}

export const NewProjectConfirmModal: React.FC<NewProjectConfirmModalProps> = ({
  currentProjectLabel,
  onConfirmRotate,
  onCancelRestore,
}) => {
  const styles = useStyles();
  const projectLabel = currentProjectLabel?.trim() || "this drafting project";

  return (
    <dialog open className={styles.backdrop} aria-modal="true" aria-labelledby="new-project-modal-title">
      <div className={styles.dialog}>
        <div id="new-project-modal-title" className={styles.title}>
          Start a new project?
        </div>
        <div className={styles.body}>
          It looks like you&rsquo;ve cleared this document. Continuing will start a fresh project: a new document ID, a
          new library and doc-type pick, and no carry-over of chat or sections from {projectLabel}.
        </div>
        <div className={styles.caveat}>
          Cancel will undo the deletion in Word and keep everything as it was. The previous project&rsquo;s history is
          preserved on the server either way.
        </div>
        <div className={styles.actions}>
          <button type="button" className={mergeClasses(styles.btn, styles.btnSecondary)} onClick={onCancelRestore}>
            Cancel — undo
          </button>
          <button type="button" className={mergeClasses(styles.btn, styles.btnPrimary)} onClick={onConfirmRotate}>
            Yes, start fresh
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default NewProjectConfirmModal;
